import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle2, Database, Fingerprint, KeyRound, Loader2, Lock, LogOut, Moon, ShieldCheck, Sun, Wifi, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Label } from '../components/ui/Label';
import { useAuth } from '../contexts/AuthContext';
import { useStudents } from '../contexts/StudentsContext';
import { useTheme } from '../contexts/ThemeContext';
import { initialsOf } from '../utils/format';
import { accountProfile } from '../utils/profile';
import { scorePassword } from '../utils/security';
import { testSupabaseConnection } from '../utils/supabase';
export function Settings() {
  const {
    user,
    logout,
    lock,
    changeMasterPassword
  } = useAuth();
  const {
    students,
    syncState
  } = useStudents();
  const {
    theme,
    setTheme
  } = useTheme();
  const navigate = useNavigate();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<{
    ok: boolean;
    detail: string;
  } | null>(null);
  const profile = accountProfile(user);
  const connected = syncState === 'live';
  const runConnectionTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const result = await testSupabaseConnection();
      setTestResult(result);
      if (result.ok) toast.success('Connection healthy');else toast.error('Connection problem', {
        description: result.detail
      });
    } finally {
      setTesting(false);
    }
  };
  const handleChangePassword = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError('');
    if (next.length < 6) {
      setError('The new master password needs at least 6 characters.');
      return;
    }
    if (next !== confirm) {
      setError('The new passwords do not match.');
      return;
    }
    setSaving(true);
    try {
      await changeMasterPassword(current, next);
      toast.success('Master password updated');
      setCurrent('');
      setNext('');
      setConfirm('');
    } catch (changeError) {
      setError(changeError instanceof Error ? changeError.message : 'Could not update the master password.');
    } finally {
      setSaving(false);
    }
  };
  const handleLogout = async () => {
    try {
      await logout();
      toast.success('Logged out safely.');
    } catch (logoutError) {
      toast.error(logoutError instanceof Error ? logoutError.message : 'Could not log out.');
    }
    navigate('/', {
      replace: true
    });
  };
  return <div className="mx-auto w-full max-w-4xl space-y-5" data-id="element-632" data-mp="ctaeg19flpxg">
      <section className="glass flex flex-col gap-4 rounded-3xl p-6 sm:flex-row sm:items-center" data-id="element-633" data-mp="hpr26ucpy2a0">
        {profile.avatarUrl ? <img src={profile.avatarUrl} alt={profile.name} className="h-16 w-16 rounded-2xl border border-[var(--glass-border)] object-cover" data-id="element-634" data-mp="22a5yzwlx05p6" /> : <span className="grid h-16 w-16 place-items-center rounded-2xl brand-gradient text-lg font-semibold text-white" data-id="element-635" data-mp="1sax5f4uyot3c">
            {initialsOf(profile.name)}
          </span>}
        <div className="min-w-0 flex-1" data-id="element-636" data-mp="2kxrg5a1z8e3m">
          <h2 className="truncate text-lg font-semibold tracking-tight text-foreground" data-id="element-637" data-mp="2l0x76uu61q1z">{profile.name}</h2>
          <p className="truncate text-sm text-muted-foreground" data-id="element-638" data-mp="2b99hayd9ch2m">{profile.email}</p>
          <p className="mt-1.5 inline-flex items-center gap-1.5 rounded-full bg-emerald-500/12 px-2.5 py-0.5 text-xs font-medium text-emerald-700 dark:text-emerald-300" data-id="element-639" data-mp="2b6wr5xxcj0eh">
            <ShieldCheck className="h-3 w-3" data-id="element-640" data-mp="33r52iqrmjc7m" />
            Google account verified
          </p>
        </div>
        <div className="flex flex-col gap-2 sm:flex-row" data-id="element-641" data-mp="2yxiy1lvm6emw">
          <Button variant="outline" className="h-9 rounded-xl" onClick={() => {
          lock();
          navigate('/security', {
            replace: true
          });
        }} data-id="element-642" data-mp="2jgko2rgl5zk4">
            <Lock className="mr-1.5 h-4 w-4" data-id="element-643" data-mp="29jy382o9f8i3" />
            Lock app
          </Button>
          <Button variant="ghost" className="h-9 rounded-xl text-destructive" onClick={handleLogout} data-id="element-644" data-mp="2eismf5q52z2q">
            <LogOut className="mr-1.5 h-4 w-4" data-id="element-645" data-mp="328d7bu5us6en" />
            Log out
          </Button>
        </div>
      </section>

      <div className="grid gap-4 lg:grid-cols-2" data-id="element-646" data-mp="375vjdtf631qv">
        <section className="glass rounded-3xl p-6" data-id="element-647" data-mp="2shs8c5m9ozv7">
          <h3 className="text-sm font-semibold tracking-tight text-foreground" data-id="element-648" data-mp="2sh8rks8cshir">Appearance</h3>
          <p className="mt-1 text-xs text-muted-foreground" data-id="element-649" data-mp="2xf0vylq6pw3u">Choose how Student Recorder looks on this device.</p>
          <div className="mt-4 grid grid-cols-2 gap-3" data-id="element-650" data-mp="3l4v2ymntoqg6">
            {(['light', 'dark'] as const).map(option => <button key={option} type="button" onClick={() => setTheme(option)} aria-pressed={theme === option} className={`flex flex-col items-start gap-3 rounded-2xl border p-4 text-left transition-all ${theme === option ? 'border-brand bg-white/70 shadow-sm dark:bg-white/10' : 'border-[var(--glass-hairline)] hover:bg-white/50 dark:hover:bg-white/5'}`} data-id="element-651" data-mp="3pyqy7hcpbz7n">
                <span className={`grid h-9 w-9 place-items-center rounded-xl ${option === 'dark' ? 'bg-slate-900 text-slate-100' : 'bg-amber-100 text-amber-700'}`} data-id="element-652" data-mp="qkrvjum0zdj5">
                  {option === 'dark' ? <Moon className="h-4 w-4" data-id="element-653" data-mp="10kiohruxtkh4" /> : <Sun className="h-4 w-4" data-id="element-654" data-mp="2fydeph9tmgy3" />}
                </span>
                <span className="text-sm font-medium capitalize text-foreground" data-id="element-655" data-mp="2g0rc3wrz458t">{option} mode</span>
              </button>)}
          </div>
        </section>

        <section className="glass rounded-3xl p-6" data-id="element-656" data-mp="18lvvh568rwbk">
          <div className="flex items-start justify-between gap-3" data-id="element-657" data-mp="1dkz4f0pcemot">
            <div className="min-w-0" data-id="element-658" data-mp="1dnl7oo57ag18">
              <h3 className="text-sm font-semibold tracking-tight text-foreground" data-id="element-659" data-mp="1ik1vticegueb">Cloud connection</h3>
              <p className="mt-1 truncate text-xs text-muted-foreground" data-id="element-660" data-mp="1mmgiyen3qgbe">Encrypted school database</p>
            </div>
            <span className={`inline-flex shrink-0 items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium ${connected ? 'bg-emerald-500/12 text-emerald-700 dark:text-emerald-300' : 'bg-amber-500/12 text-amber-700 dark:text-amber-300'}`} data-id="element-661" data-mp="px7euveskd5l">
              <Wifi className="h-3 w-3" data-id="element-662" data-mp="75en14bgkxzg" />
              {connected ? 'Connected' : 'Not connected'}
            </span>
          </div>

          <ul className="mt-4 space-y-3 text-sm" data-id="element-663" data-mp="b689rud606qr">
            <li className="flex items-center gap-3 rounded-2xl border border-[var(--glass-hairline)] p-3" data-id="element-664" data-mp="b4x6dvnmt106">
              <span className="grid h-9 w-9 place-items-center rounded-xl bg-emerald-500/12 text-emerald-600" data-id="element-665" data-mp="3l4qdhuxe0ce">
                <Database className="h-4 w-4" data-id="element-666" data-mp="3qs6vc2hfar50" />
              </span>
              <span className="min-w-0 flex-1" data-id="element-667" data-mp="3qqdn4ir5vyuw">
                <span className="block text-sm font-medium text-foreground" data-id="element-668" data-mp="3ltn87yr3f9b2">Student records</span>
                <span className="block truncate text-xs text-muted-foreground" data-id="element-669" data-mp="3gukcx60x0hga">
                  {students.length} record{students.length === 1 ? '' : 's'} stored in your account
                </span>
              </span>
            </li>
            <li className="flex items-center gap-3 rounded-2xl border border-[var(--glass-hairline)] p-3" data-id="element-670" data-mp="374qx1stqpg68">
              <span className="grid h-9 w-9 place-items-center rounded-xl bg-indigo-500/12 text-indigo-600" data-id="element-671" data-mp="334gu6p18v5vl">
                <Fingerprint className="h-4 w-4" data-id="element-672" data-mp="2y5lzaoq05qv4" />
              </span>
              <span className="min-w-0 flex-1" data-id="element-673" data-mp="2t5rdv0ozxthg">
                <span className="block text-sm font-medium text-foreground" data-id="element-674" data-mp="kvqgrzqz9omd">Account ID</span>
                <span className="block truncate font-mono text-xs text-muted-foreground" data-id="element-675" data-mp="puuqcrhtdod8">{profile.id || '—'}</span>
              </span>
            </li>
          </ul>

          <Button variant="outline" className="mt-4 h-9 w-full rounded-xl" onClick={runConnectionTest} disabled={testing} data-id="element-676" data-mp="busd5ikgbj4b">
            {testing ? <Loader2 className="mr-2 h-4 w-4 animate-spin" data-id="element-677" data-mp="23peenezvg87" /> : <Wifi className="mr-2 h-4 w-4" data-id="element-678" data-mp="6vp9a1g7jixx" />}
            {testing ? 'Testing…' : 'Test cloud connection'}
          </Button>

          {testResult && <p className={`mt-3 flex items-start gap-2 rounded-2xl p-3 text-xs ${testResult.ok ? 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-300' : 'bg-amber-500/10 text-muted-foreground'}`} data-id="element-679" data-mp="3pbt2lzsnvztm">
              {testResult.ok ? <CheckCircle2 className="mt-0.5 h-3.5 w-3.5 shrink-0" data-id="element-680" data-mp="30p3jeawmp4ii" /> : <XCircle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-600" data-id="element-681" data-mp="2vq0aughfwg5s" />}
              {testResult.detail}
            </p>}
        </section>
      </div>

      <section className="glass rounded-3xl p-6" data-id="element-682" data-mp="3al6if7w6on18">
        <h3 className="flex items-center gap-2 text-sm font-semibold tracking-tight text-foreground" data-id="element-683" data-mp="35nwszs8sobde">
          <KeyRound className="h-4 w-4 text-brand" data-id="element-684" data-mp="3jmw92bi8rhxy" />
          Master password
        </h3>
        <p className="mt-1 text-xs text-muted-foreground" data-id="element-685" data-mp="3epml70ai3ec8">
          Only a salted SHA-256 digest is stored on your account — never the password itself.
        </p>

        <form onSubmit={handleChangePassword} className="mt-5 grid gap-4 sm:grid-cols-3" noValidate data-id="element-686" data-mp="3oeo0spd4vtpo">
          <div className="space-y-2" data-id="element-687" data-mp="691anf280d70">
            <Label htmlFor="current-master" data-id="element-688" data-mp="6a2vuccyag2g">Current password</Label>
            <Input id="current-master" type="password" value={current} onChange={event => setCurrent(event.target.value)} autoComplete="current-password" className="h-10 rounded-xl" data-id="element-689" data-mp="27pue058l4bh" />
          </div>
          <div className="space-y-2" data-id="element-690" data-mp="261oxdgr25qs">
            <Label htmlFor="new-master" data-id="element-691" data-mp="2730rk5law9k">New password</Label>
            <Input id="new-master" type="password" value={next} onChange={event => setNext(event.target.value)} autoComplete="new-password" className="h-10 rounded-xl" data-id="element-692" data-mp="3tcw229oqb0o8" />
            {next.length > 0 && <p className="text-xs text-muted-foreground" data-id="element-693" data-mp="kwsvk79cri1h">Strength: {scorePassword(next).label}</p>}
          </div>
          <div className="space-y-2" data-id="element-694" data-mp="gvhbeh98zp0o">
            <Label htmlFor="confirm-master" data-id="element-695" data-mp="gufq7jyipm58">Confirm new password</Label>
            <Input id="confirm-master" type="password" value={confirm} onChange={event => setConfirm(event.target.value)} autoComplete="new-password" className="h-10 rounded-xl" data-id="element-696" data-mp="35na57svtwxje" />
          </div>

          {error && <p role="alert" className="rounded-xl bg-destructive/10 px-3 py-2 text-sm font-medium text-destructive sm:col-span-3" data-id="element-697" data-mp="1l60v5z7shjws">
              {error}
            </p>}

          <div className="sm:col-span-3" data-id="element-698" data-mp="1uw3o5zewuxv4">
            <Button type="submit" disabled={saving} className="h-10 rounded-xl brand-gradient px-5 text-white hover:opacity-95" data-id="element-699" data-mp="1zu5hdd61wdkb">
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" data-id="element-700" data-mp="28utmfidgv3l2" />}
              {saving ? 'Updating…' : 'Update master password'}
            </Button>
          </div>
        </form>
      </section>
    </div>;
}